/*
 * search-pagination.js
 * JS-owned FLAT pagination for the search product grid.
 *
 * The grid shows 24 products per page across ALL results. Shopify caps
 * {% paginate search.results by N %} at 250 items per "block" for search, so
 * the full result set is split into 250-product blocks server-side. That
 * block size is an implementation detail we deliberately hide.
 *
 * CONCATENATED-STREAM MODEL
 * -------------------------
 * We treat every block's product cards as one continuous stream:
 *   stream = block1cards ++ block2cards ++ block3cards ++ ...
 * Flat page N shows stream[(N-1)*24 : N*24]. Because pages are sliced from the
 * concatenated stream (NOT per-block), block boundaries are invisible and there
 * are NO short "runt" pages at block edges — only the true last page is short.
 * Total pages = ceil(totalProducts / 24).
 *
 * LOCK-UNTIL-MAPPED
 * -----------------
 * A 250-item block can yield anywhere from 0 to 250 visible cards once
 * non-product / ignore-tagged items are dropped, so the flat page count cannot
 * be estimated from unfetched blocks — any guess is wrong often enough to show
 * pages that don't exist (this used to show an early, too-low page count that
 * then jumped once every block was fetched, and racing a pagination click
 * against that background sweep could re-show the stale count).
 *
 * So the pager and the count stay in a loading state, and the current page
 * starts at 1, until every block is fetched and the exact count is known
 * (`mapped`). That happens once per page load; afterwards paging is instant and
 * never re-locks. A requested ?pflat= is validated (and clamped to page 1 if
 * out-of-range) only once mapping completes, never before.
 *
 * Progressive enhancement: Liquid renders the current 24 + a fallback pager for
 * no-JS/crawlers; this script takes over once it has (or is fetching) the
 * stream.
 */
(function () {
	if (window.__searchPaginationInit) return;
	window.__searchPaginationInit = true;

	var GRID_ID = "content-products";
	var PAGINATION_SELECTOR = ".search-pagination";
	var COUNT_SELECTOR = "#search-product-count";
	var COUNT_LINE_SELECTOR = "#search-count-line";
	var EMPTY_MESSAGE_SELECTOR = "#search-product-empty-message";
	var LINK_SELECTOR = ".cf__link--search-page";
	var CARD_SELECTOR = ".product-item[data-sub]";
	var WINDOW = 2;
	var JUMP = 5;
	var CONCURRENCY = 6;
	// Hard ceiling on how far the sweep will probe past the server's reported
	// block count (see extendPastSnapshot). At blockSize 250 this is far beyond
	// max_search_result_count (1800), so it can never truncate a real set.
	var MAX_BLOCKS = 40;
	var meta = null;
	var perPage = 24;
	var blockSize = 250;
	var totalBlocks = 1;
	var blockCards = {};
	var current = 1;
	var totalFlat = 1;
	var mapped = false;
	var requestedFlat = null;

	function qs(sel, root) { return (root || document).querySelector(sel); }

	function reinit() {
		var gridEl = qs("#" + GRID_ID);
		try {
			if (window.BtCurrency && typeof BtCurrency.convertSilence === "function") {
				BtCurrency.convertSilence(
					window.shopCurrency, BtCurrency.currentCurrency,
					window.jQuery ? jQuery(gridEl).find("span.money") : null
				);
			}
		} catch (e) {}
		try { if (window.BT && BT.applyCustomColorSwatches) BT.applyCustomColorSwatches("#" + GRID_ID); } catch (e) {}
		try { if (window.BT && BT.reLoadReview) BT.reLoadReview("#" + GRID_ID); } catch (e) {}
		try { if (window.BT && BT.initDealCountdown) BT.initDealCountdown("#" + GRID_ID); } catch (e) {}
		try { if (window.BT && BT.popularAddedWishlistItems) BT.popularAddedWishlistItems("#" + GRID_ID); } catch (e) {}
		rehydrateWishlist();
	}

	function rehydrateWishlist(attempt) {
		attempt = attempt || 0;
		var done = false;
		try { if (typeof iWish !== "undefined" && iWish.checkIwishColl) { iWish.checkIwishColl(); done = true; } } catch (e) {}
		try { if (typeof window.iwishCheckColl === "function") { window.iwishCheckColl(); done = true; } } catch (e) {}
		if (!done && attempt < 10) setTimeout(function () { rehydrateWishlist(attempt + 1); }, 300);
	}

	function showLoading() { try { if (window.BT && BT.showLoadingFull) BT.showLoadingFull(); } catch (e) {} }
	function hideLoading() { try { if (window.BT && BT.hideLoadingFull) BT.hideLoadingFull(); } catch (e) {} }

	function scrollToGrid() {
		var el = qs(".pg_tabs") || qs("#" + GRID_ID);
		if (!el) return;
		var top = el.getBoundingClientRect().top + window.pageYOffset - 100;
		window.scrollTo({ top: top < 0 ? 0 : top, behavior: "smooth" });
	}

	// Counts only the contiguous run of blocks starting at 1 — the same run
	// buildStream() concatenates. Summing every cached block instead would count
	// products that sit past a gap and therefore never appear in the stream,
	// producing a total that promises pages the pager can't actually render.
	function exactTotalProducts() {
		var sum = 0;
		for (var b = 1; b <= totalBlocks; b++) {
			if (!blockCards[b]) break;
			sum += blockCards[b].length;
		}
		return sum;
	}

	function recomputeTotal() {
		if (!mapped) return;
		totalFlat = Math.max(1, Math.ceil(exactTotalProducts() / perPage));
		if (current > totalFlat) current = totalFlat;
	}

	function blockURL(b) {
		var u = new URL(window.location.href);
		u.searchParams.set("page", String(b));
		u.searchParams.delete("ppage");
		u.searchParams.delete("pbase");
		u.searchParams.delete("pend");
		return u.toString();
	}

	function cardsFrom(container) {
		var nodes = container.querySelectorAll(CARD_SELECTOR);
		var out = [];
		for (var i = 0; i < nodes.length; i++) {
			var el = nodes[i].cloneNode(true);
			el.classList.remove("d-none");
			out.push(el.outerHTML);
		}
		return out;
	}

	var BLOCK_RETRIES = 2;

	function fetchBlockOnce(b) {
		return fetch(blockURL(b), { headers: { "X-Requested-With": "XMLHttpRequest" } })
			.then(function (res) { if (!res.ok) throw new Error("HTTP " + res.status); return res.text(); })
			.then(function (html) {
				var doc = new DOMParser().parseFromString(html, "text/html");
				var grid = doc.getElementById(GRID_ID);
				var cards = grid ? cardsFrom(grid) : [];
				blockCards[b] = cards;
				return cards;
			});
	}

	// A block that fails every retry means the exact count/stream can never be
	// trusted for this page load (buildStream's contiguous-from-1 model can't
	// tolerate a permanent gap) — ensureBlock rejects in that case so the caller
	// can fall back to a fresh reload rather than silently show a truncated or
	// undercounted result.
	function ensureBlock(b, attemptsLeft) {
		if (blockCards[b]) return Promise.resolve(blockCards[b]);
		if (attemptsLeft === undefined) attemptsLeft = BLOCK_RETRIES;
		return fetchBlockOnce(b).catch(function (err) {
			if (attemptsLeft <= 0) throw err;
			return ensureBlock(b, attemptsLeft - 1);
		});
	}

	function seedCurrentBlock() {
		var grid = qs("#" + GRID_ID);
		if (!grid) return;
		var cb = meta.currentBlock;
		blockCards[cb] = cardsFrom(grid);
	}

	function buildStream() {
		var stream = [];
		for (var b = 1; b <= totalBlocks; b++) {
			if (blockCards[b]) stream = stream.concat(blockCards[b]);
			else break;
		}
		return stream;
	}

	function showPage(flat, push, fromPager) {
		if (!mapped) return; // guarded again at call sites, but stay safe if called directly
		if (flat < 1) flat = 1;
		if (flat > totalFlat) flat = 1; // out-of-range manual/stale request -> page 1, not last page
		showLoading();

		var stream = buildStream();
		var start = (flat - 1) * perPage;
		var slice = stream.slice(start, start + perPage);
		var grid = qs("#" + GRID_ID);
		// The redraw removes whatever had keyboard focus in the results or the pager.
		var pager = qs(PAGINATION_SELECTOR);
		var active = document.activeElement;
		var refocus = grid && (fromPager || (active && (grid.contains(active) || (pager && pager.contains(active)))));
		if (grid) grid.innerHTML = "<!--PRODUCT-START-->" + slice.join("") + "<!--PRODUCT-END-->";

		current = flat;
		// Name the results so a screen reader says which page is showing when focus lands on them.
		if (grid && slice.length) {
			grid.setAttribute("role", "group");
			grid.setAttribute("aria-label", "Product results, page " + current + " of " + totalFlat);
		}
		renderPager();
		reinit();
		if (refocus) {
			grid.setAttribute("tabindex", "-1");
			grid.focus({ preventScroll: true });
		}
		if (push !== false) updateURL(flat);
		hideLoading();
		scrollToGrid();
	}

	function fetchBlocks(list) {
		var pending = list.filter(function (b) { return !blockCards[b]; });
		if (!pending.length) return Promise.resolve();
		var idx = 0;
		function worker() {
			if (idx >= pending.length) return Promise.resolve();
			var b = pending[idx++];
			// Do NOT swallow a failure here — after ensureBlock's own retries are
			// exhausted, propagate it so the caller can fall back to a reload
			// instead of silently continuing with a permanent gap in blockCards.
			return ensureBlock(b).then(worker);
		}
		var workers = [];
		for (var i = 0; i < Math.min(CONCURRENCY, pending.length); i++) workers.push(worker());
		return Promise.all(workers);
	}

	function updateURL(flat) {
		try {
			var loc = flatToBlockSub(flat);
			var u = new URL(window.location.href);
			if (flat <= 1) {
				// Page-1 defaults: omit the pager params entirely rather than write
				// out ppage=1&pbase=0-equivalents for a page that needs none.
				u.searchParams.delete("page");
				u.searchParams.delete("ppage");
				u.searchParams.delete("pbase");
				u.searchParams.delete("pflat");
			} else {
				u.searchParams.set("page", String(loc.block));
				u.searchParams.set("ppage", String(loc.sub));
				u.searchParams.set("pbase", String(loc.pbase));
				u.searchParams.set("pflat", String(flat));
			}
			u.searchParams.delete("pend");
			window.history.pushState({ path: u.toString(), flat: flat }, "", u.toString());
		} catch (e) {}
	}

	function flatToBlockSub(flat) {
		var target = (flat - 1) * perPage;
		var acc = 0;
		for (var b = 1; b <= totalBlocks; b++) {
			var count = blockCards[b] ? blockCards[b].length : 0;
			if (target < acc + count || b === totalBlocks) {
				var within = target - acc;
				var sub = Math.floor(within / perPage) + 1;
				return { block: b, sub: sub, pbase: Math.floor(acc / perPage) };
			}
			acc += count;
		}
		return { block: 1, sub: 1, pbase: 0 };
	}

	function pageTokens() {
		var tokens = [];
		var last = totalFlat;
		var from = Math.max(1, current - WINDOW);
		var to = Math.min(last, current + WINDOW);

		if (from > 1) {
			tokens.push({ page: 1 });
			if (from > 2) tokens.push({ ellipsis: true, page: Math.max(2, current - JUMP) });
		}
		for (var p = from; p <= to; p++) tokens.push({ page: p });
		if (to < last) {
			if (to < last - 1) tokens.push({ ellipsis: true, page: Math.min(last - 1, current + JUMP) });
			tokens.push({ page: last });
		}
		return tokens;
	}

	function btn(cls, label, page, aria, isCurrent) {
		if (isCurrent) {
			return '<span class="page button button--style1 button--circle button--current dib" aria-current="page">' + label + "</span>";
		}
		return '<span class="page"><a href="#" data-page="' + page +
			'" class="button button--style2 button--circle ' + cls + ' dib"' +
			(aria ? ' aria-label="' + aria + '"' : "") + ">" + label + "</a></span>";
	}

	function renderLockedPager() {
		var host = qs(PAGINATION_SELECTOR);
		if (!host) return;
		// Spinner only, no visible text. aria-hidden rather than an
		// aria-live/aria-busy announcement: this is a brief, self-resolving
		// transient state, not something worth interrupting a screen reader
		// user for — the real pager (plain, normally-focusable links) takes
		// over once ready with no special announcement needed.
		host.innerHTML = '<span class="page pagination-loading" aria-hidden="true"><i class="loading-ajax"></i></span>';
		host.setAttribute("aria-hidden", "true");
		host.setAttribute("data-total-pages", "");
	}

	function renderPager() {
		var host = qs(PAGINATION_SELECTOR);
		if (!host) return;
		if (!mapped) { renderLockedPager(); return; }
		host.removeAttribute("aria-hidden");
		host.style.display = totalFlat <= 1 ? "none" : "";
		if (totalFlat <= 1) { host.innerHTML = ""; return; }

		var html = "";
		if (current > 1) html += btn(LINK_SELECTOR.slice(1), '<i class="lnr-chevron-left button__prev-page"></i>', current - 1, "Previous", false);

		var toks = pageTokens();
		for (var i = 0; i < toks.length; i++) {
			var t = toks[i];
			if (t.ellipsis) {
				html += btn(LINK_SELECTOR.slice(1) + " button--dot", "...", t.page, "Jump to page " + t.page, false);
			} else {
				html += btn(LINK_SELECTOR.slice(1), String(t.page), t.page, "Go to page " + t.page, t.page === current);
			}
		}

		if (current < totalFlat) html += btn(LINK_SELECTOR.slice(1), '<i class="lnr-chevron-right button__next-page"></i>', current + 1, "Next", false);

		host.innerHTML = html;
		host.setAttribute("data-total-pages", String(totalFlat));
	}

	function renderCount() {
		var host = qs(COUNT_SELECTOR);
		if (host) {
			// The whole headline line ("N product results for X") is hidden as a
			// unit while the count is unknown — hiding just the number would leave
			// a dangling "product results for X" with a gap where a figure should
			// be. Falls back to the count element itself if the line wrapper isn't
			// present.
			var line = qs(COUNT_LINE_SELECTOR) || host;
			if (!mapped) {
				// Hidden rather than showing placeholder text while the real count
				// is unknown — no "Counting products…" text, just absent until known.
				line.classList.add("d-none");
			} else {
				host.textContent = exactTotalProducts().toLocaleString();
				line.classList.remove("d-none");
			}
		}

		// The "no products found" message is rendered server-side from only the
		// FIRST native block's count (real products can rank after that block).
		// Once every block is swept and the real total is known, correct it:
		// hide the message if products were actually found elsewhere, show it if
		// the full sweep confirms zero.
		var emptyMsg = qs(EMPTY_MESSAGE_SELECTOR);
		if (emptyMsg && mapped) {
			var total = exactTotalProducts();
			emptyMsg.classList.toggle("d-none", total > 0);
		}
	}

	function onSweepFailed() {
		// A permanent gap breaks buildStream's contiguous model, so the count is
		// untrustworthy for this page load — reload for a clean attempt. The
		// sessionStorage guard stops a systemically failing endpoint from
		// looping reloads; after one retry it gives up and stays in the loading
		// state.
		var reloadKey = "search_pagination_reload_" + (meta && meta.q ? meta.q : "");
		try {
			if (sessionStorage.getItem(reloadKey)) return;
			sessionStorage.setItem(reloadKey, "1");
		} catch (e) { return; }
		window.location.reload();
	}

	// totalBlocks is paginate.pages from a single page render, but Shopify's
	// search can return a slightly larger set on a later request — leaving the
	// sweep short by up to a block. Probe forward until a block yields no cards.
	//
	// Card counts can't detect the end: a block holds blockSize raw search
	// results, of which only some are visible products, so a full block
	// routinely yields far fewer than blockSize cards. Only zero is reliable.
	function extendPastSnapshot() {
		if (totalBlocks >= MAX_BLOCKS) return Promise.resolve();
		var next = totalBlocks + 1;
		// Single attempt, no retries: a 404/empty response here is the expected
		// way this terminates, not a failure worth retrying or reporting.
		return fetchBlockOnce(next).then(function (cards) {
			if (!cards || !cards.length) return;
			totalBlocks = next;
			return extendPastSnapshot();
		}, function () {
			// Past the end of the result set — nothing to extend to.
		});
	}

	function onSweepComplete() {
		mapped = true;
		recomputeTotal();
		renderCount();

		// Now that the real total is known, honor whatever page the URL asked
		// for (if any) — validated for the first time here. Out-of-range -> page
		// 1, not the last page, and the URL is cleaned to match.
		var target = requestedFlat && requestedFlat > 0 ? requestedFlat : current;
		if (target > totalFlat) target = 1;
		showPage(target, target !== 1 || requestedFlat > 1);
	}

	function init() {
		var dataEl = document.getElementById("search-pagination-data");
		if (!dataEl) return;
		try { meta = JSON.parse(dataEl.textContent); } catch (e) { return; }

		perPage = meta.perPage || 24;
		blockSize = meta.blockSize || 250;
		totalBlocks = meta.totalBlocks || 1;

		var u0 = new URL(window.location.href);
		var pflat = parseInt(u0.searchParams.get("pflat") || "", 10);
		requestedFlat = pflat && pflat > 0 ? pflat : null;

		// During the lock (before mapping completes) the grid is simply left as
		// the server rendered it — real, correctly-filtered content for whatever
		// native block the URL asked for, just not necessarily flat page 1. We
		// deliberately do NOT try to reslice it to "page 1" here: that would need
		// page 1's actual cards, which aren't in this seed unless this happens to
		// be block 1, and guessing would reintroduce the same kind of
		// not-yet-validated content this whole lock exists to avoid. The pager
		// and count already communicate "not ready yet"; onSweepComplete()
		// performs the first real, validated showPage() once the sweep finishes.
		current = 1;

		// Show the locked/loading state immediately — this doesn't need to wait
		// on anything, and starting it right away is strictly faster than
		// waiting until after the seed.
		renderPager();
		renderCount();

		document.addEventListener("click", function (e) {
			if (!mapped) return; // locked: ignore page-link clicks until the real count is known
			var link = e.target.closest ? e.target.closest(LINK_SELECTOR) : null;
			if (!link) return;
			var page = parseInt(link.getAttribute("data-page"), 10);
			if (!page || page < 1) return;
			e.preventDefault();
			showPage(page, true, true);
		});

		window.addEventListener("popstate", function () {
			if (!mapped) return;
			var u = new URL(window.location.href);
			var flat = parseInt(u.searchParams.get("pflat") || "", 10);
			if (!flat || flat < 1) {
				var pb = parseInt(u.searchParams.get("pbase") || "0", 10) || 0;
				var sub = parseInt(u.searchParams.get("ppage") || "1", 10) || 1;
				flat = pb + sub;
			}
			showPage(flat, false);
		});

		seedCurrentBlock();

		// Every block except the current one is a plain network fetch with no
		// dependency on this page's DOM, so start them immediately.
		var others = [];
		for (var b = 1; b <= totalBlocks; b++) {
			if (b !== meta.currentBlock) others.push(b);
		}
		// Promise.all, not a chain: a rejection from either side must reach
		// onSweepFailed, and success requires both.
		Promise.all([fetchBlocks(others), fetchBlocks([meta.currentBlock])])
			.then(extendPastSnapshot)
			.then(onSweepComplete)
			.catch(onSweepFailed);
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
